import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';

const formatLotId = (lotId, prefix) => {
  if (!lotId && lotId !== 0) return '-';
  return `${prefix}-${String(lotId).padStart(4, '0')}`;
};

// Encode lot data into a single value string for form submission
// Format: lot_id::protocol_id::start_date::bull_id
// protocol_id is still a fallback field on the lot doc (see BaseLot.ProtocolID
// on the backend), but start_date has no lot-level cache anymore — only the
// lot's active assignment has one, which this dropdown doesn't fetch — so it
// always encodes empty here. Not a regression: the backend's lots.animals
// POST handler already derives protocol_id/start_date fresh from the lot's
// active assignment itself (buildReproductionMoveExtraSet) rather than
// trusting whatever this form submits.
const encodeLotValue = (lot) => {
  const protocolId = lot.protocol_id || '';
  const startDate  = '';
  const bullId     = lot.reproduction?.bull_id || '';
  return `${lot.lot_id}::${protocolId}::${startDate}::${bullId}`;
};

// Parse encoded lot value back into components
export const parseLotValue = (value) => {
  if (!value) return null;
  const parts = value.split('::');
  if (parts.length < 3) return null;
  return {
    lot_id: parseInt(parts[0], 10),
    protocol_id: parts[1],
    start_date: parts[2],
    bull_id: parts[3] || '',
  };
};

export const LotDropdown = ({
  selected,
  onChange,
  showAll = false,
  lots = null,
  name = "lot",
  encodeValue = false,  // If true, value includes lot data for form submission
}) => {
  const { t } = useTranslation();
  const [localLots, setLocalLots] = useState([]);

  useEffect(() => {
    if (lots === null) {
      Fetch('api/data/lots?search_fields=type,status&search_values=reproduction,active', 'get', null, (data) => {
        const sorted = [...(data || [])].sort((a, b) => b.lot_id - a.lot_id);
        setLocalLots(sorted);
      });
    }
  }, [lots]);

  const lotsToDisplay = lots !== null ? lots : localLots;

  const getValue = (lot) => {
    return encodeValue ? encodeLotValue(lot) : lot.lot_id;
  };

  return (
    <select name={name} value={selected || ""} onChange={onChange}>
      {showAll && <option value="">{t("all_lots")}</option>}
      {!showAll && <option value="">{t("select_lot")}</option>}
      {lotsToDisplay.map((lot) => (
        <option key={lot._id} value={getValue(lot)}>
          {formatLotId(lot.lot_id, t('lot_prefix'))}
          {lot.name ? ` — ${lot.name}` : ''}
        </option>
      ))}
    </select>
  );
};

export default LotDropdown;
